<template>
  <div :class="{ 'mb-20': isNext }">
    <router-link
      v-if="isNext"
      :to="articleUrl"
      class="blog__read-next"
    >
      <div class="blog__read-next-header">
        <span class="blog__read-next-label">Read Next</span>
        <span class="blog__read-next-icon" aria-hidden="true">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="25"
            height="25"
            viewBox="0 0 25 25"
            fill="none"
          >
            <path
              d="M23.625 12.1875C23.625 5.87074 18.5043 0.75 12.1875 0.75C5.87074 0.75 0.75 5.87074 0.75 12.1875C0.75 18.5043 5.87074 23.625 12.1875 23.625C18.5043 23.625 23.625 18.5043 23.625 12.1875Z"
              stroke="#0A0A0A"
              stroke-width="1.5"
            />
            <path
              d="M12.3867 16.7876L16.7867 12.3875L12.3867 7.98743"
              stroke="#0A0A0A"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M7.98438 12.3875H16.7844"
              stroke="#0A0A0A"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
      </div>
      <h3 class="blog__read-next-title">{{ blogData.blogTitle }}</h3>
      <p v-if="displayDescription" class="blog__read-next-description">
        {{ displayDescription }}
      </p>
    </router-link>

    <router-link
      v-else
      :to="articleUrl"
      class="blog__card"
    >
      <div class="blog__card-media" :class="{ ['blog__card-media--' + category.slug]: !hasFeatureImage && category.slug }">
        <img
          v-if="hasFeatureImage"
          :src="cardImage"
          :alt="blogData.blogTitle"
          loading="lazy"
        />
        <div v-else class="blog__card-fallback" aria-hidden="true">
          <LogoMark></LogoMark>
        </div>
      </div>
      <div class="blog__card-body">
        <h6 class="blog__card-title">{{ blogData.blogTitle }}</h6>
        <div class="blog__card-footer">
          <div class="blog__card-tags" v-if="categories.length">
            <div
              class="blog__category"
              v-for="item in categories"
              v-bind:key="item.name"
              v-bind:class="item.class"
            >
              {{ item.name }}
            </div>
          </div>
          <time class="blog__card-date" :datetime="blogData.createdAt">{{ publishDate }}</time>
        </div>
      </div>
    </router-link>
  </div>
</template>

<script>
import blogUrl from "../../functions/blogUrl";
import BlogCategories from "../../functions/blogCategories";
import LogoMark from "@/components/Svgs/Logo/LogoMark";

const PLACEHOLDER_IMAGE = "https://yourimage.com";

const READ_NEXT_DESCRIPTION_MAX = 250;

function getPlainTextFromFirstParagraph(html) {
  if (!html) {
    return "";
  }

  const paragraphs = new DOMParser()
    .parseFromString(html, "text/html")
    .body.querySelectorAll("p");

  for (const paragraph of paragraphs) {
    const text = (paragraph.textContent || "").replace(/\s+/g, " ").trim();
    if (text) {
      return text;
    }
  }

  return "";
}

function truncateToMaxLength(text, maxLength = READ_NEXT_DESCRIPTION_MAX) {
  if (!text) {
    return "";
  }

  if (text.length <= maxLength) {
    return text;
  }

  return text.substring(0, maxLength);
}

export default {
  name: "BlogArticleUpNextListing",
  components: {
    LogoMark,
  },
  props: {
    isNext: {
      type: Boolean,
      default: false,
    },
    blogData: {
      type: Object,
      required: true,
    },
    nextDescription: {
      type: String,
      default: null,
    },
  },
  computed: {
    articleUrl() {
      return blogUrl.articlePath(this.blogData);
    },
    displayDescription() {
      return this.nextDescription || this.contentOpeningExcerpt;
    },
    contentOpeningExcerpt() {
      return truncateToMaxLength(
        getPlainTextFromFirstParagraph(this.blogData?.content)
      );
    },
    categories() {
      return BlogCategories.tagsFor(this.blogData).map((name) => {
        const slug = name.toLowerCase().replace(/\s+/g, "-");
        return {
          name,
          slug,
          class: "blog__category--" + slug,
        };
      });
    },
    category() {
      return this.categories[0] || { name: "", slug: "", class: "" };
    },
    cardImage() {
      return this.blogData?.featureImageUrl &&
        this.blogData.featureImageUrl !== PLACEHOLDER_IMAGE
        ? this.blogData.featureImageUrl
        : "";
    },
    hasFeatureImage() {
      return Boolean(this.cardImage);
    },
    publishDate() {
      const date = new Date(this.blogData.createdAt);
      return date.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
  },
};
</script>
